import type {
  TenantConfig,
  UnitSystem,
  TenantUnitLabels,
  TenantConversionFactors,
} from "./types";
import { defaultConfig } from "./default";
import { pepsicoConfig } from "./pepsico";
import { cocaColaConfig } from "./coca-cola";

export * from "./types";
export { defaultConfig } from "./default";
export { pepsicoConfig } from "./pepsico";
export { cocaColaConfig } from "./coca-cola";

/**
 * Map of all available tenant configurations
 */
const tenantConfigs: Record<string, TenantConfig> = {
  default: defaultConfig,
  pepsico: pepsicoConfig,
  "coca-cola": cocaColaConfig,
};

/**
 * Get tenant configuration by ID
 * Falls back to default config if tenant not found
 */
export function getTenantConfig(tenantId: string): TenantConfig {
  const config = tenantConfigs[tenantId.toLowerCase()];
  if (!config) {
    console.warn(`Tenant config not found for "${tenantId}", using default`);
    return defaultConfig;
  }
  return config;
}

/**
 * Get all available tenant IDs
 */
export function getAvailableTenants(): string[] {
  return Object.keys(tenantConfigs).filter((id) => id !== "default");
}

/**
 * Check if tenant exists
 */
export function tenantExists(tenantId: string): boolean {
  return tenantId.toLowerCase() in tenantConfigs;
}

/**
 * Get unit labels for a specific unit system
 */
export function getUnitLabelsForSystem(system: UnitSystem): TenantUnitLabels {
  if (system === "imperial") {
    return {
      volume: "Gal",
      volumeLarge: "Gal",
      flowRate: "gal/min",
      temperature: "°F",
      duration: "min",
    };
  }
  return {
    volume: "L",
    volumeLarge: "㎥",
    flowRate: "L/h",
    temperature: "°C",
    duration: "min",
  };
}

/**
 * Get conversion factors for a specific unit system
 */
export function getConversionFactorsForSystem(
  system: UnitSystem,
): TenantConversionFactors {
  if (system === "imperial") {
    return {
      litersToVolume: 0.264172, // Liters to gallons
      litersToVolumeLarge: 0.264172,
      flowRateConversion: 0.00440287, // L/h to gal/min
      temperatureConversion: (celsius: number) => (celsius * 9) / 5 + 32,
    };
  }
  return {
    litersToVolume: 1,
    litersToVolumeLarge: 0.001, // Liters to cubic meters
    flowRateConversion: 1,
    temperatureConversion: (celsius: number) => celsius,
  };
}

/**
 * Convert volume based on unit system
 * Uses decimal places for cubic meters, whole numbers for gallons
 */
export function convertVolume(
  liters: number,
  system: UnitSystem,
  useLargeUnit = true,
): number {
  const factors = getConversionFactorsForSystem(system);
  const factor = useLargeUnit
    ? factors.litersToVolumeLarge
    : factors.litersToVolume;
  const converted = liters * factor;

  // For cubic meters (metric large unit), use 1 decimal place
  // For gallons and liters, use whole numbers
  if (system === "metric" && useLargeUnit) {
    return Number(converted.toFixed(1));
  }
  return Number(converted.toFixed(0));
}

/**
 * Convert temperature based on unit system (whole numbers)
 */
export function convertTemperature(
  celsius: number,
  system: UnitSystem,
): number {
  const factors = getConversionFactorsForSystem(system);
  return Number(factors.temperatureConversion(celsius).toFixed(0));
}

/**
 * Format volume with unit label
 * Shows decimals for cubic meters, whole numbers for gallons/liters
 */
export function formatVolume(
  liters: number,
  system: UnitSystem,
  useLargeUnit = true,
): string {
  const value = convertVolume(liters, system, useLargeUnit);
  const labels = getUnitLabelsForSystem(system);
  const unit = useLargeUnit ? labels.volumeLarge : labels.volume;

  // Format with appropriate decimal places
  const formatted =
    system === "metric" && useLargeUnit
      ? value.toLocaleString(undefined, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
      : value.toLocaleString();

  return `${formatted} ${unit}`;
}
